import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, UserRound, UserPlus, Check, X, Search, Loader2, Gift } from "lucide-react";
import { toast } from "sonner";
import api, { mediaUrl } from "@/lib/api";
import { SocialLinks } from "@/components/SocialLinks";
import { GiftPremiumModal } from "@/components/GiftPremiumModal";

const TABS = [
  { id: "friends", label: "Friends" },
  { id: "requests", label: "Requests" },
  { id: "discover", label: "Discover" },
];

function UserCard({ u, actions, testid, showSocials = false }) {
  const navigate = useNavigate();
  return (
    <li data-testid={testid} className="flex items-center justify-between gap-4 px-5 py-4 border border-[#D9D4CC] bg-[#FDFBF7] rounded-sm hover:border-[#B7AE9F] transition-colors duration-300">
      <button
        data-testid={`open-profile-${u.id}`}
        onClick={() => navigate(`/u/${u.id}`)}
        className="flex items-center gap-4 min-w-0 text-left group"
      >
        <div className="w-12 h-12 rounded-full overflow-hidden border border-[#D9D4CC] bg-[#EAE6DF] flex items-center justify-center shrink-0">
          {u.avatar_url ? <img src={mediaUrl(u.avatar_url)} alt="" className="w-full h-full object-cover" /> : <UserRound className="w-6 h-6 text-[#B7AE9F]" />}
        </div>
        <div className="min-w-0">
          <p className="font-heading text-lg text-[#2C2A29] truncate group-hover:underline underline-offset-4">{u.name}</p>
          {showSocials && <div className="mt-1.5"><SocialLinks socials={u.socials} iconClass="w-4 h-4" gap="gap-2.5" onClickStop /></div>}
        </div>
      </button>
      <div className="flex items-center gap-2 shrink-0">{actions}</div>
    </li>
  );
}

export default function Community() {
  const [tab, setTab] = useState("friends");
  const [data, setData] = useState({ friends: [], incoming: [], outgoing: [] });
  const [discover, setDiscover] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [giftTo, setGiftTo] = useState(null);

  const loadFriends = () => api.get("/community/friends").then((res) => setData(res.data));
  const loadDiscover = (query = "") => api.get(`/community/users${query ? `?q=${encodeURIComponent(query)}` : ""}`).then((res) => setDiscover(res.data));

  useEffect(() => {
    Promise.all([loadFriends(), loadDiscover()]).finally(() => setLoading(false));
  }, []);

  const refresh = () => Promise.all([loadFriends(), loadDiscover(q)]);

  const request = async (id) => { await api.post("/community/request", { user_id: id }); toast.success("Friend request sent"); refresh(); };
  const respond = async (id, accept) => { await api.post(`/community/respond?accept=${accept}`, { user_id: id }); toast.success(accept ? "Friend added" : "Request declined"); refresh(); };
  const remove = async (id) => { await api.post("/community/remove", { user_id: id }); toast.success("Removed"); refresh(); };

  const btn = "flex items-center gap-1.5 px-3.5 py-2 text-sm rounded-sm transition-all duration-300";
  const giftBtn = (u) => (
    <button data-testid={`gift-${u.id}`} onClick={() => setGiftTo(u)} title="Gift a month of Premium" aria-label="Gift Premium"
      className={`${btn} border border-[#D9D4CC] text-[#9B7E4E] hover:bg-[#F3F0EA] hover:border-[#9B7E4E] px-2.5`}>
      <Gift className="w-4 h-4" />
    </button>
  );

  return (
    <div className="max-w-2xl mx-auto px-6 sm:px-8 pt-12 sm:pt-16 pb-24" data-testid="community-page">
      <div className="flex items-center gap-3 mb-6">
        <Users className="w-6 h-6 text-[#2C2A29]" strokeWidth={1.5} />
        <p className="font-body text-sm uppercase tracking-[0.2em] text-[#7A756E]">Community</p>
      </div>
      <h1 className="font-heading text-3xl sm:text-4xl text-[#2C2A29] tracking-tight">Fellow believers</h1>
      <p className="font-decree text-lg text-[#7A756E] mt-3">Connect with others and build one another up in the Word.</p>

      <div className="flex items-center gap-1 mt-8 border-b border-[#D9D4CC]">
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`community-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-4 py-3 text-sm transition-colors duration-300 border-b-2 -mb-px ${tab === t.id ? "border-[#2C2A29] text-[#2C2A29]" : "border-transparent text-[#7A756E] hover:text-[#2C2A29]"}`}
          >
            {t.label}
            {t.id === "requests" && data.incoming.length > 0 ? ` (${data.incoming.length})` : ""}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-20 text-center"><Loader2 className="w-7 h-7 animate-spin mx-auto text-[#2C2A29]" /></div>
      ) : (
        <div className="mt-8">
          {tab === "friends" && (
            data.friends.length === 0 ? (
              <p className="font-decree text-xl text-[#4A4744]" data-testid="friends-empty">No friends yet. Visit Discover to connect.</p>
            ) : (
              <ul className="space-y-3">
                {data.friends.map((u) => (
                  <UserCard key={u.id} u={u} testid={`friend-${u.id}`} showSocials
                    actions={<>{giftBtn(u)}<button onClick={() => remove(u.id)} className={`${btn} border border-[#D9D4CC] text-[#4A4744] hover:bg-[#EAE6DF]`}>Remove</button></>} />
                ))}
              </ul>
            )
          )}

          {tab === "requests" && (
            <>
              <p className="font-body text-xs uppercase tracking-[0.15em] text-[#7A756E] mb-3">Incoming</p>
              {data.incoming.length === 0 ? (
                <p className="font-body text-[#7A756E] mb-8">No incoming requests.</p>
              ) : (
                <ul className="space-y-3 mb-8">
                  {data.incoming.map((u) => (
                    <UserCard key={u.id} u={u} testid={`incoming-${u.id}`}
                      actions={<>
                        <button data-testid={`accept-${u.id}`} onClick={() => respond(u.id, true)} className={`${btn} bg-[#2C2A29] text-[#FAF9F6] hover:opacity-90`}><Check className="w-4 h-4" />Accept</button>
                        <button onClick={() => respond(u.id, false)} className={`${btn} border border-[#D9D4CC] text-[#4A4744] hover:bg-[#EAE6DF]`}><X className="w-4 h-4" /></button>
                      </>} />
                  ))}
                </ul>
              )}
              <p className="font-body text-xs uppercase tracking-[0.15em] text-[#7A756E] mb-3">Sent</p>
              {data.outgoing.length === 0 ? (
                <p className="font-body text-[#7A756E]">No pending sent requests.</p>
              ) : (
                <ul className="space-y-3">
                  {data.outgoing.map((u) => (
                    <UserCard key={u.id} u={u} testid={`outgoing-${u.id}`}
                      actions={<span className="font-body text-sm text-[#7A756E]">Pending</span>} />
                  ))}
                </ul>
              )}
            </>
          )}

          {tab === "discover" && (
            <>
              <div className="flex items-center gap-2 border border-[#D9D4CC] rounded-sm px-3 mb-5">
                <Search className="w-4 h-4 text-[#A39C90]" />
                <input
                  data-testid="community-search"
                  value={q}
                  onChange={(e) => { setQ(e.target.value); loadDiscover(e.target.value); }}
                  placeholder="Search by name…"
                  className="flex-1 bg-transparent py-3 text-[#2C2A29] placeholder-[#A39C90] focus:outline-none"
                />
              </div>
              {discover.length === 0 ? (
                <p className="font-body text-[#7A756E]" data-testid="discover-empty">No other members found yet.</p>
              ) : (
                <ul className="space-y-3">
                  {discover.map((u) => (
                    <UserCard key={u.id} u={u} testid={`discover-${u.id}`}
                      actions={<>
                        {giftBtn(u)}
                        {u.friend_status === "friends" ? <span className="font-body text-sm text-[#7A756E]">Friends</span> :
                        u.friend_status === "outgoing" ? <span className="font-body text-sm text-[#7A756E]">Requested</span> :
                        u.friend_status === "incoming" ? <button data-testid={`accept-${u.id}`} onClick={() => respond(u.id, true)} className={`${btn} bg-[#2C2A29] text-[#FAF9F6] hover:opacity-90`}><Check className="w-4 h-4" />Accept</button> :
                        <button data-testid={`add-${u.id}`} onClick={() => request(u.id)} className={`${btn} border border-[#D9D4CC] text-[#2C2A29] hover:bg-[#EAE6DF]`}><UserPlus className="w-4 h-4" />Add</button>}
                      </>} />
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      )}
      {giftTo && <GiftPremiumModal recipient={giftTo} onClose={() => setGiftTo(null)} />}
    </div>
  );
}
